import type { ReactNode } from 'react';
import { Printer, Download, Share2, ExternalLink, Ban, ShieldCheck, CheckCircle2, Clock, XCircle } from 'lucide-react';
import A4PrintContainer from './A4PrintContainer';
import DocumentWatermark from './DocumentWatermark';
import DocumentQRCodeBlock from './DocumentQRCodeBlock';
import LinkedIdsGrid from './LinkedIdsGrid';

interface ComplianceInfo {
  gstin?: string;
  pan?: string;
  stateCode?: string;
  cin?: string;
}

interface ProviderIds {
  nhcProviderId?: string;
  registrationNo?: string;
  licenseNo?: string;
  governmentCertNo?: string;
  accreditations?: string[];
  nmc?: string;
  qualification?: string;
  specialty?: string;
  nablCertNo?: string;
  isoCertNo?: string;
  pathologistId?: string;
  drugLicenseNo?: string;
  pharmacistId?: string;
  vehicleRegNo?: string;
  fitnessCertNo?: string;
  driverId?: string;
  paramedicId?: string;
  irdaiRegNo?: string;
  tpaId?: string;
  bloodBankLicenseNo?: string;
  fdaLicenseNo?: string;
}

export interface HeaderInfo {
  institutionName: string;
  institutionType?: string;
  institutionAddress?: string;
  institutionPhone?: string;
  institutionEmail?: string;
  institutionWebsite?: string;
  accreditation?: string;
  registrationNo?: string;
  compliance?: ComplianceInfo;
  providerIds?: ProviderIds;
}

interface Props {
  documentTitle: string;
  documentNumber: string;
  documentType: string;
  issueDate: string;
  expiryDate?: string | null;
  status: string;
  header: HeaderInfo;
  patientName: string;
  patientAge?: string | null;
  patientGender?: string | null;
  nhcId: string;
  neoVerifyId?: string | null;
  verifyUrl?: string | null;
  qrPayload?: string | null;
  documentHash?: string | null;
  verificationStatus?: string;
  registeredAt?: string | null;
  linkedIds?: { label: string; value: string }[];
  watermark?: 'DEMO' | 'REVOKED' | 'EXPIRED' | 'DRAFT' | null;
  children: ReactNode;
  onPrint?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  onRevoke?: () => void;
  showRevoke?: boolean;
}

const STATUS_BADGE: Record<string, string> = {
  ISSUED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  VERIFIED: 'bg-teal-50 text-teal-700 border-teal-200',
  REVOKED: 'bg-red-50 text-red-700 border-red-200',
  EXPIRED: 'bg-gray-100 text-gray-600 border-gray-200',
  DRAFT: 'bg-blue-50 text-blue-700 border-blue-200',
  PAID: 'bg-green-50 text-green-700 border-green-200',
  PARTIAL: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  UNPAID: 'bg-red-50 text-red-700 border-red-200',
};

function VerifyBadge({ status }: { status?: string }) {
  const s = (status || '').toUpperCase();
  if (s === 'VERIFIED') return (
    <span className="inline-flex items-center gap-0.5 text-[7px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-px rounded">
      <CheckCircle2 className="w-2 h-2" /> Verified
    </span>
  );
  if (s === 'REVOKED') return (
    <span className="inline-flex items-center gap-0.5 text-[7px] font-bold text-red-700 bg-red-50 border border-red-200 px-1 py-px rounded">
      <XCircle className="w-2 h-2" /> Revoked
    </span>
  );
  if (s === 'EXPIRED') return (
    <span className="inline-flex items-center gap-0.5 text-[7px] font-bold text-gray-600 bg-gray-50 border border-gray-200 px-1 py-px rounded">
      <XCircle className="w-2 h-2" /> Expired
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-[7px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1 py-px rounded">
      <Clock className="w-2 h-2" /> Pending
    </span>
  );
}

function shortVerifyId(id: string) {
  if (!id) return '';
  const parts = id.split('-');
  return parts.length >= 3 ? `${parts[0]}-…${parts[parts.length - 1].slice(-6)}` : id;
}

export default function OfficialDocumentLayout({
  documentTitle, documentNumber, documentType, issueDate, expiryDate,
  status, header, patientName, patientAge, patientGender, nhcId,
  neoVerifyId, verifyUrl, qrPayload, documentHash, verificationStatus,
  registeredAt, linkedIds, watermark, children,
  onPrint, onDownload, onShare, onRevoke, showRevoke,
}: Props) {
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const p = header.providerIds || {};
  const c = header.compliance || {};

  const accredBadges = p.accreditations || [];
  if (header.accreditation && !accredBadges.includes(header.accreditation)) {
    accredBadges.unshift(header.accreditation);
  }

  return (
    <div className="space-y-3">
      {/* Action Bar — hidden during print */}
      <div className="no-print flex flex-wrap gap-2 justify-end sticky top-0 z-20 bg-gray-50 py-2">
        <button onClick={onPrint || (() => window.print())} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 flex items-center gap-1.5 shadow-sm">
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
        <button onClick={onDownload || (() => window.print())} className="px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 flex items-center gap-1.5 shadow-sm">
          <Download className="w-3.5 h-3.5" /> Download PDF
        </button>
        <button onClick={onShare} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 flex items-center gap-1.5 shadow-sm">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        {verifyUrl && (
          <a href={verifyUrl} target="_blank" rel="noopener noreferrer"
            className="px-3 py-1.5 bg-white border border-gray-200 text-teal-700 text-xs font-medium rounded-lg hover:bg-teal-50 flex items-center gap-1.5 shadow-sm">
            <ExternalLink className="w-3.5 h-3.5" /> Verify
          </a>
        )}
        {showRevoke && (
          <button onClick={onRevoke} className="px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 flex items-center gap-1.5 shadow-sm">
            <Ban className="w-3.5 h-3.5" /> Revoke
          </button>
        )}
      </div>

      <A4PrintContainer>
        <div className="relative">
          {watermark && <DocumentWatermark text={watermark} />}

          <div className="relative z-20 px-4 py-3 sm:px-[14mm] sm:py-[12mm] print:px-0 print:py-0" style={{ minHeight: '270mm' }}>

            {/* ──── INSTITUTION HEADER ──── */}
            <div className="border-b-2 border-teal-700 pb-2 mb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                    <ShieldCheck className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold text-gray-900 leading-tight truncate">{header.institutionName}</p>
                    {header.institutionType && (
                      <p className="text-[7px] text-gray-500 uppercase tracking-wider font-medium">{header.institutionType}</p>
                    )}
                    {header.institutionAddress && (
                      <p className="text-[6.5px] text-gray-500 mt-px leading-tight">{header.institutionAddress}</p>
                    )}
                    {/* Contact line */}
                    {(header.institutionPhone || header.institutionEmail) && (
                      <p className="text-[6.5px] text-gray-400 leading-tight">
                        {header.institutionPhone}{header.institutionEmail && ` · ${header.institutionEmail}`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0 space-y-px">
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-teal-50 border border-teal-200 rounded">
                    <ShieldCheck className="w-2.5 h-2.5 text-teal-700" />
                    <span className="text-[7px] font-bold text-teal-800">NeoHealthCard</span>
                  </div>
                  {p.nhcProviderId && (
                    <p className="text-[6px] text-teal-600 font-mono">{p.nhcProviderId}</p>
                  )}
                  {accredBadges.length > 0 && (
                    <p className="text-[6px] text-gray-400">{accredBadges.join(' · ')}</p>
                  )}
                </div>
              </div>

              {/* Compliance row — registration, GSTIN, licenses */}
              {(header.registrationNo || c.gstin || p.registrationNo || p.licenseNo || p.nmc || p.nablCertNo || p.drugLicenseNo || p.bloodBankLicenseNo || p.irdaiRegNo) && (
                <div className="flex flex-wrap gap-x-3 gap-y-0 mt-1 pt-1 border-t border-gray-200 text-[6px] text-gray-400">
                  {(header.registrationNo || p.registrationNo) && <span>Reg: <b className="text-gray-500">{header.registrationNo || p.registrationNo}</b></span>}
                  {c.gstin && <span>GSTIN: <b className="text-gray-500">{c.gstin}</b></span>}
                  {c.pan && <span>PAN: <b className="text-gray-500">{c.pan}</b></span>}
                  {c.cin && <span>CIN: <b className="text-gray-500">{c.cin}</b></span>}
                  {p.nmc && <span>NMC: <b className="text-gray-500">{p.nmc}</b></span>}
                  {p.qualification && <span>{p.qualification}</span>}
                  {p.specialty && <span>{p.specialty}</span>}
                  {p.licenseNo && <span>Lic: <b className="text-gray-500">{p.licenseNo}</b></span>}
                  {p.nablCertNo && <span>NABL: <b className="text-gray-500">{p.nablCertNo}</b></span>}
                  {p.isoCertNo && <span>ISO: <b className="text-gray-500">{p.isoCertNo}</b></span>}
                  {p.drugLicenseNo && <span>Drug Lic: <b className="text-gray-500">{p.drugLicenseNo}</b></span>}
                  {p.pharmacistId && <span>Pharmacist: <b className="text-gray-500">{p.pharmacistId}</b></span>}
                  {p.bloodBankLicenseNo && <span>BB Lic: <b className="text-gray-500">{p.bloodBankLicenseNo}</b></span>}
                  {p.fdaLicenseNo && <span>FDA: <b className="text-gray-500">{p.fdaLicenseNo}</b></span>}
                  {p.irdaiRegNo && <span>IRDAI: <b className="text-gray-500">{p.irdaiRegNo}</b></span>}
                  {p.vehicleRegNo && <span>Vehicle: <b className="text-gray-500">{p.vehicleRegNo}</b></span>}
                  {p.fitnessCertNo && <span>Fitness: <b className="text-gray-500">{p.fitnessCertNo}</b></span>}
                  {p.governmentCertNo && <span>Govt Cert: <b className="text-gray-500">{p.governmentCertNo}</b></span>}
                </div>
              )}
            </div>

            {/* ──── DOC META ROW ──── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-0.5 text-[7.5px] mb-2 pb-1.5 border-b border-gray-200">
              <div>
                <p className="text-gray-400 font-medium uppercase tracking-wider text-[6px]">Document ID</p>
                <p className="font-mono font-bold text-gray-700 mt-px truncate" style={{ overflowWrap: 'anywhere' }}>{documentNumber}</p>
              </div>
              <div>
                <p className="text-gray-400 font-medium uppercase tracking-wider text-[6px]">Issue Date</p>
                <p className="font-bold text-gray-700 mt-px">{fmtDate(issueDate)}</p>
              </div>
              {expiryDate ? (
                <div>
                  <p className="text-gray-400 font-medium uppercase tracking-wider text-[6px]">Valid Until</p>
                  <p className="font-bold text-gray-700 mt-px">{fmtDate(expiryDate)}</p>
                </div>
              ) : <div />}
              <div>
                <p className="text-gray-400 font-medium uppercase tracking-wider text-[6px]">Status</p>
                <span className={`inline-block text-[7px] font-bold px-1.5 py-px rounded border mt-px ${STATUS_BADGE[status] || STATUS_BADGE.ISSUED}`}>
                  {status}
                </span>
              </div>
            </div>

            {/* ──── DOCUMENT TITLE ──── */}
            <div className="text-center my-2">
              <p className="text-[6.5px] text-gray-400 uppercase tracking-[0.2em] font-semibold mb-px">{documentType}</p>
              <h1 className="text-sm font-bold text-teal-800 tracking-tight leading-tight">{documentTitle}</h1>
              <div className="w-10 h-0.5 bg-teal-600 mx-auto mt-0.5" />
            </div>

            {/* ──── PATIENT INFO + QR ──── */}
            <div className="flex items-start justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
              <div className="space-y-0.5 min-w-0">
                <div className="flex items-baseline gap-2">
                  <p className="text-[6.5px] text-gray-400 font-medium uppercase">Patient</p>
                  <p className="text-[11px] font-bold text-gray-900">{patientName}</p>
                </div>
                <div className="flex flex-wrap gap-2 text-[7.5px] text-gray-600">
                  {patientAge && <span>Age: <b className="text-gray-800">{patientAge}</b></span>}
                  {patientGender && <span>Gender: <b className="text-gray-800">{patientGender}</b></span>}
                  <span>NHC ID: <b className="text-teal-700 font-mono">{nhcId}</b></span>
                </div>
              </div>

              {/* Compact QR + verify badge */}
              <div className="flex flex-col items-center gap-0.5 shrink-0">
                <DocumentQRCodeBlock qrPayload={qrPayload} verifyUrl={verifyUrl} size="sm" />
                {neoVerifyId && <VerifyBadge status={verificationStatus} />}
                {neoVerifyId && <span className="text-[6px] font-mono text-gray-400">{shortVerifyId(neoVerifyId)}</span>}
              </div>
            </div>

            {/* ──── BODY CONTENT (from template) ──── */}
            <div className="a4-body">
              {children}
            </div>

            {/* ──── LINKED IDS — compact ──── */}
            {linkedIds && linkedIds.length > 0 && (
              <LinkedIdsGrid ids={linkedIds} />
            )}

            {/* ──── COMPACT FOOTER ──── */}
            <div className="a4-keep-together mt-auto pt-1.5">
              {/* Bottom banner — single line with NeoVerify inline */}
              <div className="pt-1.5 border-t border-teal-700 flex items-center justify-between text-[6px] text-gray-400">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-2.5 h-2.5 text-teal-700" />
                  Generated by <b className="text-teal-700">NeoHealthCard</b>
                  {neoVerifyId && (
                    <span className="ml-1">· NeoVerify {shortVerifyId(neoVerifyId)}</span>
                  )}
                </span>
                <span>Digitally generated · verify.neohealthcard.com</span>
              </div>
            </div>
          </div>
        </div>
      </A4PrintContainer>

      {/* Consent notice — hidden during print */}
      <p className="no-print text-[10px] text-gray-400 text-center">
        This document is subject to patient consent. Sharing requires explicit authorization. All access is audit-logged.
      </p>
    </div>
  );
}
